/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useState } from "react";
import { Row } from "react-table";
import { FormattedMessage, useIntl } from "react-intl";
import { Card } from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { useNavigate, useSearchParams } from "react-router-dom";

import "../../shipper/Quotes/Quotes.scss";
import { getQuoteRequests, getQuotationCount } from "api";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import Filter from "components/organisms/Filter/Filter";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import DataTable from "components/organisms/DataTable/DataTable";
import ListViewTour from "components/tours/listView/listViewTour";
import { getDateParams, getObjectByKeys } from "helpers/commonHelpers";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import errorToast from "helpers/errorToast";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";
import useProviderQuoteFilter from "./useProviderQuoteFilter";
import ProviderQuotesColumn from "./ProviderQuotesColumn";

interface IStatusCount {
  all: number;
  active: number;
  pending: number;
  confirmation_required: number;
  declined: number;
}

const ADDITIONAL_COLUMNS = [
  "created",
  "modified-date",
  "confirmation_datetime",
  "dangerous_goods",
  "total_weight_kg",
  "total_weight_lbs",
  "total_volume_cbm",
  "pickup_date",
  "stop1_date",
  "export_clearance",
  "transit_clearance",
  "import_clearance",
  "service_requirement",
  "incoterms",
  "insurance",
  "custom_field_01",
  "custom_field_02",
  "reason_code",
  "cost_center",
  "related_shipments_count",
];

function ProviderQuotes() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentLocations = useSortedPaymentLocations();
  const paymentLocationsHashMap = useMemo<Record<string, any>>(
    () => getObjectByKeys(paymentLocations, "value"),
    [paymentLocations],
  );
  const { actions } = useStateMachine({ updateShipmentId, shipmentCreatedBy });
  const { config: filterConfig } = useProviderQuoteFilter();
  const { persistedColumnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "quote_request" });
  const status: string = searchParams.get("display_status") || "";

  const [quotationStatusCount, setQuotationStatusCount] = useState<IStatusCount>({
    all: 0,
    active: 0,
    pending: 0,
    confirmation_required: 0,
    declined: 0,
  });

  const columns = useMemo(() => ProviderQuotesColumn(intl, status), [status, paymentLocationsHashMap]);

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("quote_requests_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("quote_requests_hidden_columns", JSON.stringify(ADDITIONAL_COLUMNS));
    return ADDITIONAL_COLUMNS;
  };

  const { instance, fetching, totalData } = useTableRequest(getQuoteRequests, columns, {
    hiddenColumns: getHiddenColumns(),
    columnOrder: persistedColumnOrder,
    params: (tableInstance: any) => ({
      display_status: status,
      properties: ADDITIONAL_COLUMNS.join(","),
      ...(tableInstance?.state?.sortBy?.[0]?.id === "location" && {
        sort_by: `payment_location:${tableInstance?.state?.sortBy?.[0]?.desc ? "DESC" : "ASC"}`,
      }),
    }),
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.display_status?.length && { display_status: globalFilter.display_status.join(",") }),
      ...(globalFilter.status?.length && { status: globalFilter.status.join(",") }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location.join(",") }),
      ...(globalFilter.freight_mode?.length && { freight_modes: globalFilter.freight_mode.join(",") }),
      ...(globalFilter.pickup_date?.type && getDateParams(globalFilter.pickup_date, "pickup_date")),
      ...(globalFilter.pickup_city?.length && { pickup_city: globalFilter.pickup_city }),
      ...(globalFilter.delivery_city?.length && { delivery_city: globalFilter.delivery_city }),
      ...(globalFilter.delivery_date?.type && getDateParams(globalFilter.delivery_date, "delivery_date")),
      ...(globalFilter.shipper?.length && { shipper: globalFilter.shipper }),
      ...(globalFilter.assigned_team?.length && { assigned_team: globalFilter.assigned_team.join(",") }),
    }),
    deps: [status],
  });

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.quoting.view_quote_requests) navigate("/access-denied");
  }, []);

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  useEffect(() => {
    getQuotationCount()
      .then((res) => {
        setQuotationStatusCount(res.data);
      })
      .catch((err) => errorToast(err));
  }, []);

  function updateShipmentId(state: any, payload: { shipmentId: string; shipmentTitle: string }) {
    return {
      ...state,
      shipmentId: payload.shipmentId,
      shipmentTitle: payload.shipmentTitle,
    };
  }

  function shipmentCreatedBy(state: any, payload: { createdBy: string }) {
    return {
      ...state,
      createdBy: payload.createdBy,
    };
  }

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.quote_request_reference;
    const title = row.original?.quote_request_title;
    actions.updateShipmentId({
      shipmentId: reference,
      shipmentTitle: title,
    });
    actions.shipmentCreatedBy({
      createdBy: row.original?.created_by,
    });

    const isDraft = status === "draft" || status === "system draft" || status === "drafts";
    if ((e.ctrlKey || e.metaKey) && !isDraft) window.open(`/shipment/${reference}`, "_blank");
    else if (isDraft) {
      navigate(`/quote-request?draft_id=${reference}`);
    } else navigate(`/shipment/${reference}`, { state: { data: row.original?.created_by } });
  };

  return (
    <>
      <ListViewTour />
      <Card body className="shadow-none quote-container">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mr-3">
            <FormattedMessage id="quote.quoteRequestsTabTitle" defaultMessage="Quote Requests" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            filterKey="query"
            eventContext="Quote Request List"
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="quote-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                {
                  eventKey: "active",
                  title: `${intl.formatMessage({
                    id: "tabs.pendingQuotation",
                    defaultMessage: "Pending Quotation",
                  })} (${quotationStatusCount.active})`,
                },
                {
                  eventKey: "pending",
                  title: `${intl.formatMessage({
                    id: "tabs.quoted",
                    defaultMessage: "Quoted",
                  })} (${quotationStatusCount.pending})`,
                },
                {
                  eventKey: "declined",
                  title: `${intl.formatMessage({
                    id: "tabs.declined",
                    defaultMessage: "Declined",
                  })} (${quotationStatusCount.declined})`,
                },
                {
                  eventKey: "confirmation_required",
                  title: `${intl.formatMessage({
                    id: "tabs.pendingRevalidation",
                    defaultMessage: "Pending Revalidation",
                  })} (${quotationStatusCount.confirmation_required})`,
                },
                {
                  eventKey: "archived",
                  title: intl.formatMessage({
                    id: "tabs.archived",
                    defaultMessage: "Archived",
                  }),
                },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="quote-request"
                eventContext="Quote Request List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter
                  columns={instance.allColumns}
                  eventContext="Quote Request List"
                  listViewName="quote_requests"
                />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default ProviderQuotes;
